import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiClientError } from '../../lib/api';
import { Badge } from '../../atoms';
import { useLawEdition } from '../useLawEdition';

interface MatterProperty {
  property_id: string;
  property_name: string;
  tax_year: number;
  docket_no: string | null;
}
export interface Matter {
  id: string;
  name: string;
  case_no: string | null;
  docket_no: string | null;
  status: string | null;
  client_id: string;
  client_name: string;
  properties: MatterProperty[];
  property_count: number;
}

const STATUS_VARIANT: Record<string, 'success' | 'neutral' | 'info' | 'warning'> = {
  open: 'success', settled: 'neutral', closed: 'info',
};
export function statusVariant(s: string | null): 'success' | 'neutral' | 'info' | 'warning' {
  return STATUS_VARIANT[(s || '').toLowerCase()] || 'neutral';
}

function NotAvailable() {
  return (
    <div style={{ padding: 'var(--sp-6)', color: 'var(--color-ink-3)' }}>
      Matters aren’t available for your firm.
    </div>
  );
}

/**
 * The client → property → matter roll-up (L-03). Groups the firm's matters by client; each
 * matter links to its detail. A single-property matter simply shows one property — no drill.
 */
export function MattersIndex() {
  const { mattersOn } = useLawEdition();
  const q = useQuery({
    queryKey: ['law-master-chart'],
    queryFn: ({ signal }) => api.get<{ matters: Matter[] }>('/api/law/master-chart', signal),
    enabled: mattersOn,
    retry: false,
  });

  if (!mattersOn) return <NotAvailable />;
  if (q.isLoading) return <div style={{ padding: 'var(--sp-6)', color: 'var(--color-ink-3)' }}>Loading matters…</div>;
  if (q.error) {
    if (q.error instanceof ApiClientError && q.error.status === 404) return <NotAvailable />;
    return <div style={{ padding: 'var(--sp-6)', color: 'var(--color-status-red, #b4432e)' }}>Couldn’t load matters.</div>;
  }

  const matters = q.data?.matters ?? [];
  const byClient = new Map<string, { client_name: string; matters: Matter[] }>();
  for (const m of matters) {
    if (!byClient.has(m.client_id)) byClient.set(m.client_id, { client_name: m.client_name, matters: [] });
    byClient.get(m.client_id)!.matters.push(m);
  }
  const groups = [...byClient.values()].sort((a, b) => a.client_name.localeCompare(b.client_name));

  return (
    <div style={{ maxWidth: 1000 }}>
      <h1 style={{ fontSize: 'var(--fs-3xl)', fontWeight: 'var(--fw-semibold)', letterSpacing: 'var(--ls-tight)', margin: 0 }}>Matters</h1>
      <p style={{ color: 'var(--color-ink-3)', margin: 'var(--sp-2) 0 var(--sp-6)', maxWidth: '64ch' }}>
        Every lawsuit the firm is running, rolled up client → property → matter. One matter can span
        several of a client’s properties; a single-property client shows plainly.
      </p>

      {groups.length === 0 && (
        <div style={{ color: 'var(--color-ink-3)' }}>No matters yet.</div>
      )}

      {groups.map((g) => (
        <div key={g.client_name} style={{ marginBottom: 'var(--sp-5)', border: '1px solid var(--color-border)', borderRadius: 'var(--r, 10px)', overflow: 'hidden', background: 'var(--color-surface, #fff)' }}>
          <div style={{ padding: 'var(--sp-3) var(--sp-4)', background: 'var(--color-bg)', fontWeight: 'var(--fw-semibold)', borderBottom: '1px solid var(--color-border)' }}>
            {g.client_name}
          </div>
          {g.matters.map((m) => (
            <div key={m.id} style={{ padding: 'var(--sp-3) var(--sp-4)', borderBottom: '1px solid var(--color-border)', display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <Link to={`/law/matters/${m.id}`} style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--brand-primary)', textDecoration: 'none' }}>{m.name}</Link>
              {m.case_no && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{m.case_no}</span>}
              <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm, 13px)' }}>
                {m.property_count} propert{m.property_count === 1 ? 'y' : 'ies'}
              </span>
              <span style={{ marginLeft: 'auto' }}>
                <Badge variant={statusVariant(m.status)}>{m.status || '—'}</Badge>
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
